import { toShortCode, type LanguageCode } from '@s2w/types';

export const LOCALE_STORAGE_KEY = 's2w.locale';

export interface DetectLocaleInput {
  readonly supportedLanguages: readonly LanguageCode[];
  readonly search?: string;
  readonly storage?: Pick<Storage, 'getItem'>;
  readonly userLocale?: string | null;
}

const fromSupported = (
  candidate: string | null | undefined,
  supported: readonly LanguageCode[],
): LanguageCode | null => {
  const short = toShortCode(candidate);
  if (short && supported.includes(short)) return short;
  return null;
};

const fromQueryString = (
  search: string,
  supported: readonly LanguageCode[],
): LanguageCode | null => {
  try {
    const params = new URLSearchParams(search);
    return fromSupported(params.get('lang') ?? params.get('language'), supported);
  } catch {
    return null;
  }
};

const fromStorage = (
  storage: Pick<Storage, 'getItem'> | undefined,
  supported: readonly LanguageCode[],
): LanguageCode | null => {
  if (!storage) return null;
  try {
    return fromSupported(storage.getItem(LOCALE_STORAGE_KEY), supported);
  } catch {
    return null;
  }
};

/**
 * Cascade: URL `?lang=` → SSO `user.locale` → localStorage → first supported.
 * `postMessage` from the parent is handled at runtime (see AuthProvider), not here.
 */
export const detectLocale = (input: DetectLocaleInput): LanguageCode => {
  const { supportedLanguages, search, storage, userLocale } = input;
  const fallback = supportedLanguages[0]!;

  return (
    fromQueryString(search ?? '', supportedLanguages) ??
    fromSupported(userLocale, supportedLanguages) ??
    fromStorage(storage, supportedLanguages) ??
    fallback
  );
};
